import { Injectable } from "@angular/core";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import * as dayjsUtcPlugin from "dayjs/plugin/utc";

import { InvalidOperationException } from "../exceptions/exceptions";

import { LanguageService } from "./language/language.service";

dayjs.extend(customParseFormat);
dayjs.extend(dayjsUtcPlugin);

@Injectable({
  providedIn: "root",
})
export class DateTimeService {
  constructor(private readonly _languageService: LanguageService) {}

  static getFirstDayOfNextMonthDate() {
    return DateTimeService.getFirstDayOfFutureMonthDate(1);
  }

  static getFirstDayOfFutureMonthDate(numberOfMonthsInTheFuture: number) {
    return dayjs()
      .utc()
      .add(numberOfMonthsInTheFuture, "month")
      .startOf("month")
      .toDate();
  }

  // 2024-01-01
  static tryToIsoDate(date: Date | undefined): string | undefined;
  static tryToIsoDate(date: Date | undefined | null): string | undefined | null;
  static tryToIsoDate(date: Date | undefined | null) {
    return date === undefined || date === null
      ? date
      : DateTimeService.toIsoDate(date);
  }

  // 2024-01-01
  static toIsoDate(date: Date) {
    return dayjs(date).format(ISO_8601_DATE_FORMAT);
  }

  // 01.01.2024
  tryToShortDisplayDate(date: Date | undefined): string | undefined;
  tryToShortDisplayDate(
    date: Date | undefined | null,
  ): string | undefined | null;
  tryToShortDisplayDate(date: Date | undefined | null) {
    return date === undefined || date === null
      ? date
      : this.toShortDisplayDate(date);
  }

  // 01.01.2024
  toShortDisplayDate(date: Date) {
    const language = this._languageService.getLanguage();
    return new Intl.DateTimeFormat(language).format(date);
  }

  static tryParseIsoDate(isoDate: string | undefined): Date | undefined;
  static tryParseIsoDate(
    isoDate: string | undefined | null,
  ): Date | undefined | null;
  static tryParseIsoDate(isoDate: string | undefined | null) {
    return isoDate ? DateTimeService.parseIsoDate(isoDate) : isoDate;
  }

  static parseIsoDate(isoDate: string) {
    const parsedDate = dayjs(isoDate, "YYYY-MM-DD", true);

    if (!parsedDate.isValid()) {
      throw new InvalidOperationException(
        `The provided date with the value '${isoDate}' is in the wrong format. Please provide date in the '${ISO_8601_DATE_FORMAT}' format`,
      );
    }

    const date = new Date(isoDate);

    return date;
  }
}

/**
 * We use this format mainly for the transmission between the backend and the frontend.
 * This format can then be converted to a Date object and if presented to the user can be formatted with the following
 * format @see SHORT_DISPLAY_DATE_FORMAT
 *
 * https://en.wikipedia.org/wiki/ISO_8601
 */
export const ISO_8601_DATE_FORMAT = "YYYY-MM-DD";
